import { z } from 'zod';
import type { Site } from './types';
import { LOCALES } from 'common/json/schema/locale';
import sitesJSON from 'railsRoot/config/sites.json';

// Throws rather than substituting, unlike `schema/extracted`: the file ships
// with the build, so a shape this refuses is a mistake in the file.
const site = z
  .object({
    locale: z.enum(LOCALES),
    base_domain: z.string(),
    // `nullish`: most sites omit `sales_person.phone` rather than write null.
    country_in_path: z.string().nullish(),
    default_og_image: z.string().nullish(),
    default_search_bounds: z.object({
      south: z.number(),
      west: z.number(),
      north: z.number(),
      east: z.number()
    }),
    default_map_center: z.tuple([z.number(), z.number()]),
    iso_3166_1_alpha_2: z.string().nullish(),
    iso_3166_1_alpha_3: z.string().nullish(),
    profile_time_zone: z.string(),
    partner_support: z.object({
      mail: z.string(),
      phone: z.string().nullish()
    }),
    sales_person: z.object({
      team_mail: z.string(),
      first_name: z.string(),
      image: z.string(),
      mail: z.string(),
      name: z.string(),
      phone: z.string().nullish()
    }),
    e_invoice_operators: z.array(z.object({ name: z.string(), id: z.string() }))
  })
  .transform(row => ({
    locale: row.locale,
    baseDomain: row.base_domain,
    countryInPath: row.country_in_path ?? null,
    defaultOgImage: row.default_og_image ?? null,
    defaultSearchBounds: row.default_search_bounds,
    defaultMapCenter: row.default_map_center,
    iso31661Alpha2: row.iso_3166_1_alpha_2 ?? null,
    iso31661Alpha3: row.iso_3166_1_alpha_3 ?? null,
    profileTimeZone: row.profile_time_zone,
    partnerSupport: {
      mail: row.partner_support.mail,
      phone: row.partner_support.phone ?? null
    },
    salesPerson: {
      teamMail: row.sales_person.team_mail,
      firstName: row.sales_person.first_name,
      image: row.sales_person.image,
      mail: row.sales_person.mail,
      name: row.sales_person.name,
      phone: row.sales_person.phone ?? null
    },
    eInvoiceOperators: row.e_invoice_operators
  }));

// Shared by every caller, so a write here would change it for all of them.
const SITES: ReadonlyArray<Readonly<Site>> = Object.entries(
  z.record(z.string(), site).parse(sitesJSON)
).map(([key, row]) => ({ key, ...row }));

export function allSites(): ReadonlyArray<Readonly<Site>> {
  return SITES;
}
